/**
 * BO_JSON_DATA (+ Global Contact) → Appendix A/B 양식 values / criteria 매핑.
 */
import { resolveBoGender, resolveBoRace, resolveBoNationality } from './boAppendixFields';
import {
  composeMyAddressLine,
  formatMyAddressLine,
  parseMalaysianAddress,
  resolveCountry,
} from '@/shared/utils/malaysianAddress';

export type BoAppendixPctState = {
  direct_shares: string;
  direct_voting: string;
  indirect_shares: string;
  indirect_voting: string;
  combined_shares: string;
  combined_voting: string;
};

export type BoAppendixCriteriaState = {
  cat: { direct: boolean; indirect: boolean; both: boolean; control: boolean };
  crit: {
    da: boolean; db: boolean;
    ia: boolean; ib: boolean;
    ba: boolean; bb: boolean;
    c: boolean; d: boolean; e: boolean; f: boolean;
  };
  /** Criteria % — values 와 별도로 criteria 에 두어 체크박스와 함께 UI 갱신 */
  pct: BoAppendixPctState;
};

export function emptyBoAppendixPct(): BoAppendixPctState {
  return {
    direct_shares: '',
    direct_voting: '',
    indirect_shares: '',
    indirect_voting: '',
    combined_shares: '',
    combined_voting: '',
  };
}

export function emptyBoAppendixCriteria(): BoAppendixCriteriaState {
  return {
    cat: { direct: false, indirect: false, both: false, control: false },
    crit: {
      da: false, db: false, ia: false, ib: false,
      ba: false, bb: false, c: false, d: false, e: false, f: false,
    },
    pct: emptyBoAppendixPct(),
  };
}

function truthy(v: unknown): boolean {
  return v === true || v === 1 || v === '1'
    || String(v ?? '').toLowerCase() === 'true'
    || String(v ?? '').toLowerCase() === 'yes';
}

function str(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function normalizeAddress(raw: unknown): string {
  return formatMyAddressLine(String(raw || ''));
}

function composeAddress(parts: {
  line?: unknown;
  town?: unknown;
  state?: unknown;
  postcode?: unknown;
  country?: unknown;
}): string {
  const lineRaw = str(parts.line);
  const parsed = parseMalaysianAddress(lineRaw);
  const postcode = str(parts.postcode) || parsed.postcode;
  const town = str(parts.town) || parsed.town;
  const state = str(parts.state) || parsed.state;
  const country = resolveCountry(parts.country as string)
    || resolveCountry(parsed.country)
    || ((postcode || town || state) ? 'MALAYSIA' : '');
  const street = parsed.street || lineRaw;
  return composeMyAddressLine({
    line: normalizeAddress(street),
    postcode,
    town,
    state,
    country,
  });
}

/** YYYY-MM-DD / Date → DD/MM/YYYY */
export function formatBoDateDisplay(raw: unknown): string {
  const s = str(raw);
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
  }
  return s;
}

function addrFromService(rows: unknown): string {
  if (!Array.isArray(rows) || !rows.length) return '';
  const a = rows[0] as Record<string, unknown>;
  return composeAddress({
    line: a.Address || a.address || a.composed || a.value,
    postcode: a.PostCode || a.postCode,
    town: a.Town || a.town,
    state: a.State || a.state,
    country: a.Country || a.country || a.CountryName || a.countryName,
  });
}

function addrFromBusiness(rows: unknown): string {
  if (!Array.isArray(rows) || !rows.length) return '';
  const a = rows[0] as Record<string, unknown>;
  return composeAddress({
    line: a.BusAddress || a.busAddress || a.Address || a.address,
    postcode: a.BusPostCode || a.postCode,
    town: a.BusTown || a.town,
    state: a.BusState || a.state,
    country: a.BusCountry || a.Country || a.country,
  });
}

/** SSM RaceId → CONTACT MY Race 콤보 value */
function raceFromId(id: unknown): string {
  const n = Number(id);
  const map: Record<number, string> = { 1: 'Malay', 2: 'Chinese', 3: 'Indian', 4: 'Other Race' };
  return map[n] || '';
}

function genderFromIsMale(v: unknown): string {
  if (v === 1 || v === '1' || v === true || String(v).toLowerCase() === 'true') return 'MALE';
  if (v === 0 || v === '0' || v === false || String(v).toLowerCase() === 'false') return 'FEMALE';
  return '';
}

function phoneFromContact(c: Record<string, unknown>): string {
  const hp0 = str(c.HPNo0 || c.hpNo0 || c.areaCode);
  const hp1 = str(c.HPNo1 || c.hpNo1 || c.mobileNo || c.MOBILE_NO);
  if (hp0 && hp1) return `${hp0}-${hp1}`;
  return hp1 || hp0 || str(c.TelNo1 || c.phone);
}

function pct(v: unknown): string {
  if (v == null || v === '') return '';
  const n = Number(String(v).replace(/%/g, '').trim());
  if (!Number.isFinite(n)) return str(v);
  return String(n);
}

/** list row / API 의 boJsonData | BO_JSON_DATA (object | JSON string) 파싱 */
export function parseBoJsonPayload(row: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!row) return {};
  const raw = row.boJsonData ?? row.BO_JSON_DATA ?? row.bo_json_data;
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw);
      return o && typeof o === 'object' && !Array.isArray(o) ? o as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** MY NRIC: ICNo0-ICNo1-ICNo2 (예: 840427-01-5650) */
function nricFromWho(who: Record<string, unknown>): string {
  const p0 = str(who.ICNo0);
  const p1 = str(who.ICNo1);
  const p2 = str(who.ICNo2);
  if (p0 && p1 && p2) return `${p0}-${p1}-${p2}`;
  if (p0 && p1) return `${p0}-${p1}`;
  return p0 || str(who.PassportNo) || str(who.OldICNo);
}

/**
 * ownership + list meta criteria 병합.
 * BOCriteriaA/B + Percent / OwnPercent 보강.
 */
function resolveOwnershipFromRow(row: Record<string, unknown>): {
  ownership: Record<string, unknown>;
  boTypeId: unknown;
} {
  const json = parseBoJsonPayload(row);
  const ownership: Record<string, unknown> = {
    ...((json.ownership && typeof json.ownership === 'object' && !Array.isArray(json.ownership))
      ? json.ownership as Record<string, unknown>
      : {}),
  };

  // list 응답에 flatten 된 ownership 우선 보강
  if (row.ownership && typeof row.ownership === 'object' && !Array.isArray(row.ownership)) {
    Object.assign(ownership, row.ownership as Record<string, unknown>);
  }

  const cls = (json.classification && typeof json.classification === 'object'
    ? json.classification
    : {}) as Record<string, unknown>;
  const boTypeId = cls.BOTypeId ?? cls.boTypeId ?? row.boTypeId;

  // list 응답 criteria[{code,percent}] 보강
  if (Array.isArray(row.criteria)) {
    for (const item of row.criteria as Array<{ code?: string; percent?: string }>) {
      const code = str(item.code).toUpperCase();
      if (!code) continue;
      const flagKey = `BOCriteria${code}`;
      const pctKey = `BOCriteria${code}Percent1`;
      if (!truthy(ownership[flagKey])) ownership[flagKey] = 1;
      if ((ownership[pctKey] == null || ownership[pctKey] === '') && item.percent != null && item.percent !== '') {
        ownership[pctKey] = item.percent;
      }
    }
  }

  // OwnPercent → A/B % 폴백
  const ownPct = pct(ownership.OwnPercent ?? row.ownershipPercent);
  if (ownPct) {
    if (!ownership.OwnPercent) ownership.OwnPercent = ownPct;
    if (truthy(ownership.BOCriteriaA) && (ownership.BOCriteriaAPercent1 == null || ownership.BOCriteriaAPercent1 === '')) {
      ownership.BOCriteriaAPercent1 = ownPct;
    }
    if (truthy(ownership.BOCriteriaB) && (ownership.BOCriteriaBPercent1 == null || ownership.BOCriteriaBPercent1 === '')) {
      ownership.BOCriteriaBPercent1 = ownPct;
    }
  }

  // Direct(type=1) 인데 A/B 플래그가 전혀 없고 OwnPercent 만 있으면 A+B 로 간주
  const typeId = Number(boTypeId);
  const anyFlag = ['A', 'B', 'C', 'D', 'E', 'F'].some((L) => truthy(ownership[`BOCriteria${L}`]));
  if (typeId === 1 && !anyFlag && ownPct) {
    ownership.BOCriteriaA = 1;
    ownership.BOCriteriaB = 1;
    ownership.BOCriteriaAPercent1 = ownership.BOCriteriaAPercent1 || ownPct;
    ownership.BOCriteriaBPercent1 = ownership.BOCriteriaBPercent1 || ownPct;
  }

  return { ownership, boTypeId };
}

/**
 * ownership + BOTypeId → Appendix criteria UI 상태
 * BOTypeId: 1 Direct / 2 Indirect / 3 Direct&Indirect / 4 Control
 */
export function mapOwnershipToCriteria(
  ownership: Record<string, unknown> | null | undefined,
  boTypeId: unknown,
): BoAppendixCriteriaState {
  const out = emptyBoAppendixCriteria();
  const o = ownership && typeof ownership === 'object' ? ownership : {};
  const typeId = Number(boTypeId);

  const a = truthy(o.BOCriteriaA);
  const b = truthy(o.BOCriteriaB);
  const c = truthy(o.BOCriteriaC);
  const d = truthy(o.BOCriteriaD);
  const e = truthy(o.BOCriteriaE);
  const f = truthy(o.BOCriteriaF);

  if (typeId === 1 || (!typeId && (a || b) && !c && !d && !e && !f)) {
    out.cat.direct = true;
    out.crit.da = a;
    out.crit.db = b;
  } else if (typeId === 2) {
    out.cat.indirect = true;
    out.crit.ia = a;
    out.crit.ib = b;
  } else if (typeId === 3) {
    out.cat.both = true;
    out.crit.ba = a;
    out.crit.bb = b;
  } else if (typeId === 4 || c || d || e || f) {
    out.cat.control = true;
    out.crit.c = c;
    out.crit.d = d;
    out.crit.e = e;
    out.crit.f = f;
  } else {
    // type 없이 A/B 만 → Direct 로 가정
    if (a || b) {
      out.cat.direct = true;
      out.crit.da = a;
      out.crit.db = b;
    }
  }

  return out;
}

/** Criteria % → values 키 */
export function mapOwnershipPercentsToValues(
  ownership: Record<string, unknown> | null | undefined,
  boTypeId: unknown,
): Record<string, string> {
  const o = ownership && typeof ownership === 'object' ? ownership : {};
  const typeId = Number(boTypeId);
  const a1 = pct(o.BOCriteriaAPercent1);
  const a2 = pct(o.BOCriteriaAPercent2);
  const b1 = pct(o.BOCriteriaBPercent1);
  const b2 = pct(o.BOCriteriaBPercent2);
  const own = pct(o.OwnPercent);
  const out: Record<string, string> = {};

  if (typeId === 2) {
    if (a1 || a2 || (truthy(o.BOCriteriaA) && own)) out.pct_indirect_shares = a1 || a2 || own;
    if (b1 || b2 || (truthy(o.BOCriteriaB) && own)) out.pct_indirect_voting = b1 || b2 || own;
  } else if (typeId === 3) {
    if (a1 || a2 || (truthy(o.BOCriteriaA) && own)) out.pct_combined_shares = a1 || a2 || own;
    if (b1 || b2 || (truthy(o.BOCriteriaB) && own)) out.pct_combined_voting = b1 || b2 || own;
  } else {
    // Direct (1) or default
    if (a1 || a2 || (truthy(o.BOCriteriaA) && own)) out.pct_direct_shares = a1 || a2 || own;
    if (b1 || b2 || (truthy(o.BOCriteriaB) && own)) out.pct_direct_voting = b1 || b2 || own;
  }
  return out;
}

export function isCorporateBoRow(row: Record<string, unknown> | null | undefined): boolean {
  if (!row) return false;
  const cat = str(row.categoryCd || row.CATEGORY_CD).toUpperCase();
  if (cat === 'B09001') return true;
  if (cat === 'B09002') return false;
  const json = parseBoJsonPayload(row);
  const who = json.whoIs as Record<string, unknown> | undefined;
  if (who && (who.IsCompany !== undefined || who.isCompany !== undefined)) {
    return truthy(who.IsCompany ?? who.isCompany);
  }
  return false;
}

export function isIndividualBoRow(row: Record<string, unknown> | null | undefined): boolean {
  if (!row) return false;
  return !isCorporateBoRow(row);
}

/** SMReasonId: 1 No BO / 2 Cannot identify / 3 In process */
export type BoSmReasonId = 0 | 1 | 2 | 3;

export const SM_REASON_LABELS: Record<1 | 2 | 3, string> = {
  1: 'The company has no beneficial owner',
  2: 'Beneficial owner cannot be identified',
  3: 'In the process of obtaining ownership information',
};

export function normalizeSmReasonId(raw: unknown): BoSmReasonId {
  const n = Number(raw);
  if (n === 1 || n === 2 || n === 3) return n;
  return 0;
}

/** BO_JSON sm.SMReasonId (+ list flatten) */
export function smReasonIdFromRow(row: Record<string, unknown> | null | undefined): BoSmReasonId {
  if (!row) return 0;
  const json = parseBoJsonPayload(row);
  const sm = (json.sm && typeof json.sm === 'object' ? json.sm : {}) as Record<string, unknown>;
  return normalizeSmReasonId(
    sm.SMReasonId ?? sm.smReasonId
    ?? row.smReasonId ?? row.SM_REASON_ID,
  );
}

/** classification.IsSeniorManagement 또는 SMReasonId 1–3 → Senior Management 레코드 */
export function isSeniorManagementBoRow(row: Record<string, unknown> | null | undefined): boolean {
  if (!row) return false;
  if (truthy(row.isSeniorManagement ?? row.IS_SENIORMANAGEMENT ?? row.is_senior_management)) return true;
  const json = parseBoJsonPayload(row);
  const cls = (json.classification && typeof json.classification === 'object'
    ? json.classification
    : {}) as Record<string, unknown>;
  if (truthy(cls.IsSeniorManagement ?? cls.isSeniorManagement)) return true;
  return smReasonIdFromRow(row) > 0;
}

export function smReasonLabel(id: BoSmReasonId): string {
  if (id === 1 || id === 2 || id === 3) return SM_REASON_LABELS[id];
  return '';
}

/**
 * BO list row + optional contact → Appendix C Senior Management 인적사항 values
 */
export function mapBoRowToSmParticulars(
  row: Record<string, unknown>,
  contact?: Record<string, unknown> | null,
): Record<string, string> {
  const bo = mapBoRowToAppendixParticulars(row, contact);
  const json = parseBoJsonPayload(row);
  const until = (json.untilDates && typeof json.untilDates === 'object' ? json.untilDates : {}) as Record<string, unknown>;
  const c = contact && typeof contact === 'object' ? contact : {};
  const reasonId = smReasonIdFromRow(row);

  return {
    sm_full_name: bo.bo_full_name || '',
    sm_office_address: bo.bo_business_address
      || str(c.primaryAddress || c.officeAddress || c.alternateAddress),
    sm_email: bo.bo_email || '',
    sm_residential_address: bo.bo_residential_address || '',
    sm_phone: bo.bo_contact_no || '',
    sm_nationality: bo.bo_nationality || '',
    sm_dob: bo.bo_dob || '',
    sm_race: bo.bo_race || '',
    sm_gender: bo.bo_gender || '',
    sm_id_no: bo.bo_id_no || '',
    sm_position: bo.bo_position || '',
    sm_appointment_date: formatBoDateDisplay(
      until.BOBecomeDate || row.becomingDate || row.appointmentDate || c.appointmentDate,
    ),
    sm_reason_id: reasonId ? String(reasonId) : '',
    sm_reason: smReasonLabel(reasonId),
  };
}

/**
 * BO list row + optional contact detail → Appendix BO 인적사항 values
 */
export function mapBoRowToAppendixParticulars(
  row: Record<string, unknown>,
  contact?: Record<string, unknown> | null,
): Record<string, string> {
  const json = parseBoJsonPayload(row);
  const who = (json.whoIs && typeof json.whoIs === 'object' ? json.whoIs : {}) as Record<string, unknown>;
  const until = (json.untilDates && typeof json.untilDates === 'object' ? json.untilDates : {}) as Record<string, unknown>;
  const addr = (json.address && typeof json.address === 'object' ? json.address : {}) as Record<string, unknown>;
  const contactJson = (json.contact && typeof json.contact === 'object' ? json.contact : {}) as Record<string, unknown>;
  const c = contact && typeof contact === 'object' ? contact : {};

  const residentialFromJson = addrFromService(addr.service_address);
  const businessFromJson = addrFromBusiness(addr.business_address);

  const addrs = Array.isArray((c as any).addresses) ? (c as any).addresses : [];
  const addrKind = (x: any) => [x?.type, x?.label, x?.originalLabel].map((s) => String(s || '')).join(' ');
  const resAddr = addrs.find((x: any) => /residential|registered|home/i.test(addrKind(x)));
  const bizAddr = addrs.find((x: any) => /business|office|service/i.test(addrKind(x)));
  const flat = (x: any) => normalizeAddress(x?.composed || x?.value || x?.address);

  const out: Record<string, string> = {
    bo_full_name: str(who.BeneficialName || row.fullName || row.name || row.contactName || c.contactName || c.name),
    bo_residential_address: residentialFromJson
      || flat(resAddr)
      || normalizeAddress(c.residentialAddress || c.primaryAddress || row.primaryAddress),
    bo_business_address: businessFromJson || flat(bizAddr),
    bo_email: str(contactJson.Email || c.email || c.contactEmail || row.email || row.contactEmail),
    bo_nationality: resolveBoNationality(
      c.nationalityName || c.nationality || c.nationalityCd || row.nationalityName
      || who.CitizenshipName || '',
    ),
    bo_dob: formatBoDateDisplay(who.DateOfBirth || c.dateOfBirth || row.dateOfBirth),
    bo_race: resolveBoRace(c.race || c.raceText || c.contactProfileRace || raceFromId(who.RaceId)),
    bo_gender: resolveBoGender(genderFromIsMale(who.IsMale ?? c.isMale ?? c.ISMALE) || c.gender || c.sexText),
    bo_id_no: nricFromWho(who)
      || str(c.passportNo || c.nric || c.identificationNo || c.contactRegNo || row.regNo || row.contactRegNo),
    bo_contact_no: phoneFromContact(contactJson)
      || phoneFromContact(c as Record<string, unknown>)
      || [str(row.areaCode), str(row.mobileNo)].filter(Boolean).join('-'),
    bo_position: str(who.Occupation || c.occupation || c.positionName),
    // JSON BOBecomeDate 우선 (DB BECOMING_DATE 와 다를 수 있음)
    bo_date_became: formatBoDateDisplay(
      until.BOBecomeDate || row.becomingDate || row.appointmentDate,
    ),
  };

  // 빈 값 제거하지 않음 — 호출측에서 put 시 skip
  return out;
}

export function mapBoRowToCriteriaAndPercents(row: Record<string, unknown>): {
  criteria: BoAppendixCriteriaState;
  percents: Record<string, string>;
} {
  const { ownership, boTypeId } = resolveOwnershipFromRow(row);
  const criteria = mapOwnershipToCriteria(ownership, boTypeId);
  const percents = mapOwnershipPercentsToValues(ownership, boTypeId);

  // A/B 체크됐는데 Percent1 비어 있으면 OwnPercent / list ownershipPercent 폴백
  const ownFallback = pct(
    ownership.OwnPercent
    ?? ownership.ownPercent
    ?? row.ownershipPercent
    ?? row.ownership_percent,
  );
  if (ownFallback) {
    if (criteria.crit.da && !percents.pct_direct_shares) percents.pct_direct_shares = ownFallback;
    if (criteria.crit.db && !percents.pct_direct_voting) percents.pct_direct_voting = ownFallback;
    if (criteria.crit.ia && !percents.pct_indirect_shares) percents.pct_indirect_shares = ownFallback;
    if (criteria.crit.ib && !percents.pct_indirect_voting) percents.pct_indirect_voting = ownFallback;
    if (criteria.crit.ba && !percents.pct_combined_shares) percents.pct_combined_shares = ownFallback;
    if (criteria.crit.bb && !percents.pct_combined_voting) percents.pct_combined_voting = ownFallback;
  }

  criteria.pct = {
    direct_shares: percents.pct_direct_shares || '',
    direct_voting: percents.pct_direct_voting || '',
    indirect_shares: percents.pct_indirect_shares || '',
    indirect_voting: percents.pct_indirect_voting || '',
    combined_shares: percents.pct_combined_shares || '',
    combined_voting: percents.pct_combined_voting || '',
  };

  return { criteria, percents };
}
